import { lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { type Crumb, HeaderCrumbs } from './components/ui/Page'
import { useCan } from './lib/auth'
import { ProjectLayout, ProjectPicker } from './pages/project/ProjectLayout'

// Pages load on demand: the first page needs only the layout and its own code.
// A tab opened before a deploy may ask for a page file of the previous build
// that is gone: then it loads the app again, once (not in a loop when the
// file is missing for another reason).
function page<K extends string>(load: () => Promise<Record<K, ComponentType<any>>>, name: K) { // eslint-disable-line @typescript-eslint/no-explicit-any
  return lazy(() => load().then((m) => ({ default: m[name] }), (err) => {
    const key = 'xc-reloaded-for-chunk'
    let last = 0
    try { last = Number(sessionStorage.getItem(key) || 0) } catch { /* storage blocked */ }
    if (Date.now() - last > 60_000) {
      try { sessionStorage.setItem(key, String(Date.now())) } catch { /* storage blocked */ }
      window.location.reload()
      return new Promise<never>(() => {}) // the page reloads
    }
    throw err
  }))
}

const OverviewPage = page(() => import('./pages/OverviewPage'), 'OverviewPage')
const MaintenancePage = page(() => import('./pages/MaintenancePage'), 'MaintenancePage')
const CustomersPage = page(() => import('./pages/customers/CustomersPage'), 'CustomersPage')
const CustomerDetailPage = page(() => import('./pages/customers/CustomerDetailPage'), 'CustomerDetailPage')
const CustomerUsersPage = page(() => import('./pages/customers/CustomerUsersPage'), 'CustomerUsersPage')
const ProjectsPage = page(() => import('./pages/projects/ProjectsPage'), 'ProjectsPage')
const ProjectDetailPage = page(() => import('./pages/projects/ProjectDetailPage'), 'ProjectDetailPage')
const ClustersPage = page(() => import('./pages/clusters/ClustersPage'), 'ClustersPage')
const SitesPage = page(() => import('./pages/clusters/SitesPage'), 'SitesPage')
const ClusterDetailPage = page(() => import('./pages/clusters/ClusterDetailPage'), 'ClusterDetailPage')
const SiteDetailPage = page(() => import('./pages/clusters/SiteDetailPage'), 'SiteDetailPage')
const RackPage = page(() => import('./pages/clusters/RackPage'), 'RackPage')
const PlanMoved = page(() => import('./pages/clusters/RackPage'), 'PlanMoved')
const NetworkPage = page(() => import('./pages/network/NetworkPage'), 'NetworkPage')
const HardwarePage = page(() => import('./pages/HardwarePage'), 'HardwarePage')
const MachinesPage = page(() => import('./pages/machines/MachinesPage'), 'MachinesPage')
const MachineNewPage = page(() => import('./pages/machines/MachineNewPage'), 'MachineNewPage')
const DiscoveredPage = page(() => import('./pages/machines/DiscoveredPage'), 'DiscoveredPage')
const MachineDetailPage = page(() => import('./pages/machines/MachineDetailPage'), 'MachineDetailPage')
const NetworksPage = page(() => import('./pages/networks/NetworksPage'), 'NetworksPage')
const NetworkDetailPage = page(() => import('./pages/networks/NetworkDetailPage'), 'NetworkDetailPage')
const SwitchesPage = page(() => import('./pages/SwitchesPage'), 'SwitchesPage')
const AgentsPage = page(() => import('./pages/AgentsPage'), 'AgentsPage')
const VMsPage = page(() => import('./pages/VMsPage'), 'VMsPage')
const JobsPage = page(() => import('./pages/JobsPage'), 'JobsPage')
const BIOSProfilesPage = page(() => import('./pages/BIOSProfilesPage'), 'BIOSProfilesPage')
const FirmwareManagement = page(() => import('./components/FirmwareManagement'), 'FirmwareManagement')
const SSHKeysPage = page(() => import('./pages/SSHKeysPage'), 'SSHKeysPage')
const BaselineManagement = page(() => import('./components/BaselineManagement'), 'BaselineManagement')
const DNSManagement = page(() => import('./components/DNSManagement'), 'DNSManagement')
const CertificateManagement = page(() => import('./components/CertificateManagement'), 'CertificateManagement')
const ImageManagement = page(() => import('./components/ImageManagement'), 'ImageManagement')
const UsersRolesPage = page(() => import('./pages/admin/UsersRolesPage'), 'UsersRolesPage')
const AuditPage = page(() => import('./pages/admin/AuditPage'), 'AuditPage')
const ProjectOverview = page(() => import('./pages/project/ProjectPages'), 'ProjectOverview')
const ProjectServers = page(() => import('./pages/project/ProjectPages'), 'ProjectServers')
const ProjectNetworks = page(() => import('./pages/project/ProjectPages'), 'ProjectNetworks')
const ProjectVolumes = page(() => import('./pages/project/ProjectPages'), 'ProjectVolumes')
const ProjectActivity = page(() => import('./pages/project/ProjectPages'), 'ProjectActivity')
const ProjectAccess = page(() => import('./pages/project/ProjectPages'), 'ProjectAccess')
const ProjectSettings = page(() => import('./pages/project/ProjectPages'), 'ProjectSettings')

// Pages that still bring their own heading get only the breadcrumbs.
function Legacy({ crumbs, children }: { crumbs: Crumb[]; children: ReactNode }) {
  return (
    <div>
      <HeaderCrumbs crumbs={crumbs} />
      {children}
    </div>
  )
}

// Moved: an old address - its page is elsewhere now (the query stays)
function Moved({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={{ pathname: to, search }} replace />
}

function Administer({ children }: { children: ReactNode }) {
  const can = useCan()
  return can.canAdminister ? <>{children}</> : <p className="text-sm text-muted-foreground">This page needs the operator or owner role.</p>
}

const loading = <p className="text-sm text-muted-foreground">Loading…</p>

export default function App() {
  return (
    <Suspense fallback={loading}>
      <Routes>
        <Route path="p" element={<ProjectPicker />} />
        <Route path="p/:project" element={<ProjectLayout />}>
          <Route index element={<ProjectOverview />} />
          <Route path="servers" element={<ProjectServers />} />
          <Route path="networks" element={<ProjectNetworks />} />
          <Route path="volumes" element={<ProjectVolumes />} />
          <Route path="activity" element={<ProjectActivity />} />
          <Route path="access/:tab?" element={<ProjectAccess />} />
          <Route path="settings" element={<ProjectSettings />} />
        </Route>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/ops/overview" replace />} />
          <Route path="ops">
            <Route index element={<Navigate to="/ops/overview" replace />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="maintenance/:tab?" element={<MaintenancePage />} />
            <Route path="updates" element={<Navigate to="/ops/maintenance" replace />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id/:tab?" element={<CustomerDetailPage />} />
            <Route path="customer-users" element={<CustomerUsersPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:id/:tab?" element={<ProjectDetailPage />} />
            <Route path="sites" element={<SitesPage />} />
            <Route path="sites/:id/:tab?" element={<SiteDetailPage />} />
            <Route path="sites/:id/racks/:rack/:view?" element={<RackPage />} />
            <Route path="sites/:id/plan/:view?" element={<PlanMoved />} />
            <Route path="clusters" element={<ClustersPage />} />
            <Route path="clusters/:id" element={<ClusterDetailPage />} />
            <Route path="plan" element={<Moved to="/ops/sites" />} />
            <Route path="network/:view?" element={<NetworkPage />} />
            <Route path="machines" element={<MachinesPage />} />
            <Route path="machines/new" element={<MachineNewPage />} />
            <Route path="machines/discovered" element={<DiscoveredPage />} />
            <Route path="machines/switches" element={<SwitchesPage />} />
            <Route path="machines/switches/ports" element={<SwitchesPage />} />
            <Route path="machines/:id/:tab?/:sub?" element={<MachineDetailPage />} />
            <Route path="networks" element={<NetworksPage />} />
            <Route path="networks/:id" element={<NetworkDetailPage />} />
            <Route path="switches" element={<Moved to="/ops/machines/switches" />} />
            <Route path="switches/ports" element={<Moved to="/ops/machines/switches/ports" />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="vms" element={<VMsPage />} />
            <Route path="jobs" element={<JobsPage />} />
            <Route path="firmware" element={<Legacy crumbs={[{ label: 'Library' }, { label: 'Firmware' }]}><FirmwareManagement /></Legacy>} />
            <Route path="ssh-keys" element={<SSHKeysPage />} />
            <Route path="bios-profiles" element={<BIOSProfilesPage />} />
            <Route path="hardware/:tab?" element={<HardwarePage />} />
            <Route path="baselines" element={<Legacy crumbs={[{ label: 'Library' }, { label: 'Baselines' }]}><BaselineManagement /></Legacy>} />
            <Route path="dns" element={<Legacy crumbs={[{ label: 'Services' }, { label: 'DNS' }]}><DNSManagement /></Legacy>} />
            <Route path="certificates" element={<Legacy crumbs={[{ label: 'Services' }, { label: 'Certificates' }]}><CertificateManagement /></Legacy>} />
            <Route path="images" element={<Legacy crumbs={[{ label: 'Library' }, { label: 'OS images' }]}><ImageManagement /></Legacy>} />
            <Route path="users" element={<Administer><UsersRolesPage tab="people" /></Administer>} />
            <Route path="users/roles" element={<Administer><UsersRolesPage tab="roles" /></Administer>} />
            <Route path="users/local" element={<Moved to="/ops/users" />} />
            <Route path="audit" element={<Administer><AuditPage /></Administer>} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

function NotFound() {
  return (
    <div>
      <h1 className="font-display text-[28px] font-bold text-primary">Page not found</h1>
      <p className="mt-2 text-sm text-muted-foreground">This address does not exist (any more). <Link className="underline" to="/ops/overview">Go to the overview</Link></p>
    </div>
  )
}
